/**
 * 🪢 **Ropes — a lead from a hand to a sheep, or from a peg to a sheep.** D128.
 *
 * 🎯 From the table haugur said yes to: *"drawn as a rope that sags, from your
 * hand to the sheep's neck, or from the peg to the sheep. Everyone near sees
 * it."*
 *
 * ## 🔴 Triangles on a material the game already compiles
 *
 * `LineBasicMaterial.linewidth` is ignored, and a fat-line material would be a
 * NEW material — which is what turned the game black on 2026-09-01. ✅ **So a
 * rope is a thin ribbon built into the geometry, the way the cracks are**, on
 * `MeshBasicMaterial`, the break-box's own setup. ⚠️ **Two ribbons crossed**, so
 * it has width whichever way you look along it.
 *
 * ## The sag
 *
 * A parabola between the two ends, lowest in the middle: {@link ropePoint}.
 * **Slack rope sags more**: the drop is a share of what is left of the rope's
 * length after the straight line — a rope pulled nearly taut is nearly straight.
 */

import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  type Scene,
} from 'three';

/** Points along one rope. */
const SEGMENTS = 14;
/** 🪢 How thick a rope is drawn, metres. A braided wool lead. */
const WIDTH = 0.035;
/** 🎣 How thick a fishing line is drawn — thin, but still visible at ten metres. */
const LINE_WIDTH = 0.012;
/** How long a lead is, metres — the snap distance. A rope shorter than the gap is taut. */
const ROPE_LENGTH = 12;
/** How far above the ground a rope lying on it is drawn, so the grass does not swallow it. */
const GROUND_CLEAR = 0.03;

export interface RopeEnds {
  readonly key: string;
  readonly a: { readonly x: number; readonly y: number; readonly z: number };
  readonly b: { readonly x: number; readonly y: number; readonly z: number };
  /** How much rope there is between them, metres — the sag comes from the slack. */
  readonly length?: number;
  /**
   * 🟫 **Where the ground is under the rope, metres** — slack LIES on it rather
   * than sagging through it. 🔴 **Found by baking the picture**: a 5 m tether
   * with the sheep 2.5 m away hung a metre deep, and vanished into the grass.
   */
  readonly floor?: number;
  /**
   * 🎣 **A fishing line, not a rope** — D129. Thinner and paler. 🔴 **Found by
   * baking the picture**: the first line was the lead's rope, and read as one.
   */
  readonly thin?: boolean;
}

/**
 * 🪢 **A point `t` of the way along a sagging rope** — 0 at `a`, 1 at `b`.
 *
 * ⚠️ **Exported so a test can hold it to its promises**: the ends are exactly
 * the ends, the middle is the lowest point, and a taut rope is straight.
 */
export function ropePoint(
  a: RopeEnds['a'],
  b: RopeEnds['b'],
  t: number,
  length = ROPE_LENGTH,
  floor = -Infinity,
): { x: number; y: number; z: number } {
  const span = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
  const slack = Math.max(0, length - span);
  /* The drop at the middle: most of the slack hangs, but never more than the
     rope could reach — a 3 m lead does not trail a metre on the ground. */
  const sag = Math.min(slack * 0.35, 1.2);
  return {
    x: a.x + (b.x - a.x) * t,
    /* 🟫 Never below the ground — a slack rope lies on the grass. */
    y: Math.max(floor + GROUND_CLEAR, a.y + (b.y - a.y) * t - sag * 4 * t * (1 - t)),
    z: a.z + (b.z - a.z) * t,
  };
}

/** Every rope in the world, one mesh each, re-shaped every frame. */
export class Ropes {
  private readonly live = new Map<string, Mesh>();
  /** 🔴 One material for every rope, built once — never made mid-game. */
  private readonly material = new MeshBasicMaterial({ color: 0x5a4632, side: DoubleSide });
  /** 🎣 A pale grey line — the same material class, one more instance, built once. */
  private readonly lineMaterial = new MeshBasicMaterial({ color: 0xb8b2a4, side: DoubleSide });

  private readonly scene: Scene;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /**
   * Draw exactly these ropes, in render space (world metres minus `origin`).
   * A rope not in the list is taken away.
   */
  update(ropes: readonly RopeEnds[], origin: { x: number; y: number; z: number }): void {
    const seen = new Set<string>();
    for (const rope of ropes) {
      seen.add(rope.key);
      let mesh = this.live.get(rope.key);
      if (mesh === undefined) {
        const geometry = new BufferGeometry();
        geometry.setAttribute(
          'position',
          new BufferAttribute(new Float32Array(SEGMENTS * 2 * 6 * 3), 3),
        );
        mesh = new Mesh(geometry, rope.thin === true ? this.lineMaterial : this.material);
        /* ⚠️ Its bounding sphere is recomputed each frame below, so it never
           culls itself while it swings across the view. */
        this.scene.add(mesh);
        this.live.set(rope.key, mesh);
      }
      this.shape(mesh, rope, origin);
    }
    for (const [key, mesh] of this.live) {
      if (seen.has(key)) continue;
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      this.live.delete(key);
    }
  }

  private shape(mesh: Mesh, rope: RopeEnds, origin: { x: number; y: number; z: number }): void {
    const attr = mesh.geometry.getAttribute('position') as BufferAttribute;
    const out = attr.array as Float32Array;
    const h = (rope.thin === true ? LINE_WIDTH : WIDTH) / 2;
    let o = 0;
    const put = (x: number, y: number, z: number): void => {
      out[o++] = x - origin.x;
      out[o++] = y - origin.y;
      out[o++] = z - origin.z;
    };
    for (let i = 0; i < SEGMENTS; i++) {
      const p = ropePoint(rope.a, rope.b, i / SEGMENTS, rope.length, rope.floor);
      const q = ropePoint(rope.a, rope.b, (i + 1) / SEGMENTS, rope.length, rope.floor);
      /* Ribbon one: widened sideways, across the rope's own direction. */
      const dx = q.x - p.x;
      const dz = q.z - p.z;
      const flat = Math.hypot(dx, dz) || 1;
      const sx = (-dz / flat) * h;
      const sz = (dx / flat) * h;
      put(p.x - sx, p.y, p.z - sz);
      put(p.x + sx, p.y, p.z + sz);
      put(q.x + sx, q.y, q.z + sz);
      put(p.x - sx, p.y, p.z - sz);
      put(q.x + sx, q.y, q.z + sz);
      put(q.x - sx, q.y, q.z - sz);
      /* Ribbon two: widened upward, so it shows edge-on too. */
      put(p.x, p.y - h, p.z);
      put(p.x, p.y + h, p.z);
      put(q.x, q.y + h, q.z);
      put(p.x, p.y - h, p.z);
      put(q.x, q.y + h, q.z);
      put(q.x, q.y - h, q.z);
    }
    attr.needsUpdate = true;
    mesh.geometry.computeBoundingSphere();
  }

  dispose(): void {
    for (const mesh of this.live.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
    }
    this.live.clear();
    this.material.dispose();
    this.lineMaterial.dispose();
  }
}
